import React, {useState, useEffect, useRef} from 'react';
import styled, {css} from 'styled-components';
import {addWeeks, addDays, addMonths, startOfWeek, format, startOfMonth, getDate, isBefore, isAfter, parse, differenceInDays} from 'date-fns';

import {Label3} from '@bootcamp/web/src/components/Typography/next';

import {getWeeksToDisplay} from '../helpers';
import {RoundButton} from '@bootcamp/web/src/components/UI';

import {CalendarLtr} from '@styled-icons/fluentui-system-regular/CalendarLtr';
import {ChevronLeft} from '@styled-icons/fluentui-system-filled/ChevronLeft';
import {ChevronRight} from '@styled-icons/fluentui-system-filled/ChevronRight';
import {ChevronDown} from '@styled-icons/fluentui-system-filled/ChevronDown';

import {
  Calendar,
  CalendarWeek,
  CalendarHeaderContainer,
  CalendarHeaderRow,
  CalendarHeaderMonth,
  CalenderHeaderDayRow,
  MobileCalendarDay,
} from './shared';

const StyledCalendar = styled(Calendar)`
  gap: 4px;
  background: white;

  ${({theme}) => theme.mediaQueries.mobileL} {
    gap: 8px;
  }
`;

// Styles for the container and input
const Container = styled.div`
  position: relative;
  display: inline-block;
  width: 100%;
`;
const InputWrapper = styled.div`
  position: relative;
  z-index: 1;
`;
const InlineWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  position: relative;
  z-index: 1;
`;
const InlineText = styled(Label3)`
  color: ${({theme}) => theme.colors.typographyPalette.primary};
  font-weight: 400;
  ${({theme}) => theme.mediaQueries.tablet} {
    font-size: 16px;
  }
`;
export const CalendarIconBareWrapper = styled.div`
  padding: 4px;
  background: ${({theme}) => theme.colors.neutralsPalette.extraLight};
  border-radius: 8px;

  &:hover {
    background: ${({theme}) => theme.colors.neutralsPalette.light};
  }
`;
export const CalendarIcon = styled(CalendarLtr)`
  position: absolute;
  top: calc(50% - 10px);
  right: 16px;
  width: 20px;
  height: 20px;
  color: ${({theme}) => theme.colors.typographyPalette.primary};
  cursor: pointer;
  z-index: -1;
`;
const CalendarIconBare = styled(CalendarIcon)`
  position: relative;
  right: 0;
  top: 0;
`;
const StyledInput = styled.input`
  padding: 12px 16px;
  border-radius: 8px;
  background: transparent;
  border: 1px solid ${({theme}) => theme.colors.borderPalette.primary};
  color: ${({theme}) => theme.colors.typographyPalette.primary};
  width: 100%;
  font-size: 16px;
  cursor: pointer;
  font-family: proxima-nova;
  font-size: 16px;
  font-weight: 400;
  line-height: 16px;

  &:focus {
    outline: none;
    border-color: ${({theme}) => theme.colors.borderPalette.secondary};
  }

  ${({disabled}) => disabled && css`
    background: ${({theme}) => theme.colors.neutralsPalette.offWhite};
    color: ${({theme}) => theme.colors.neutralsPalette.grey};
    cursor: not-allowed;
  `}
`;
const PickerWrapper = styled.div`
  position: absolute;
  left: 4px;
  background: white;
  border: 1px solid ${({theme}) => theme.colors.borderPalette.primary};
  border-radius: 8px;
  box-shadow: 0px 8px 20px 0px #0000001F;
  z-index: 10;
  width: auto;
  user-select: none;

  ${({theme}) => theme.mediaQueries.mobileL} {
    left: -24px;
    width: 100%;
  }
`;
const CalendarContainer = styled.div`
  padding: 16px;
`;
const StyledCalendarHeaderContainer = styled(CalendarHeaderContainer)`
  position: relative;
  background: white;
`;
const StyledCalendarHeaderRow = styled(CalendarHeaderRow)`
  padding: 0;
  margin-bottom: 12px;
  `;
const StyledCalendarHeaderMonth = styled(CalendarHeaderMonth)`
  display: flex;
  justify-content: space-between;
  width: 100%;
`;
const StyledCalendarHeaderDayRow = styled(CalenderHeaderDayRow)`
  padding: 0;
  border-top: none;
  background: white;
  gap: 8px;
  margin-bottom: 12px;
`;
const StyledCalendarHeaderDay = styled(MobileCalendarDay)`
  padding: 0;
  text-transform: uppercase;
  font-size: 12px;
  font-weight: 700;
  line-height: 12px;
  cursor: ${({multi}) => multi ? 'pointer' : 'default'};

  ${({theme}) => theme.mediaQueries.mobileL} {
    width: 30px;
    height: 30px;
    font-size: 12px;
    line-height: 12px;
    padding: 0;
  }
`;
const StyledMobileCalendarDay = styled(MobileCalendarDay)`
  ${({theme}) => theme.mediaQueries.mobileL} {
    width: 30px;
    height: 30px;
    font-size: 12px;
    line-height: 12px;
    padding: 0;
  }
`;
const StyledCalendarWeek = styled(CalendarWeek)`
  border: 0;
  padding: 0;
  background: white;
  gap: 8px;
`;
const MonthSelector = styled.input`
  background: transparent;
  border: none;
  outline: none;
  font-family: proxima-nova;
  font-size: 16px;
  line-height: 20px;
  font-weight: 700;
  cursor: pointer;
  appearance: none;
  color: transparent;
  user-select: none;
  z-index: 0;
`;
const FormattedMonthDisplay = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  font-family: proxima-nova;
  font-size: 16px;
  line-height: 20px;
  font-weight: 700;
  z-index: -1;
  color: ${({theme}) => theme.colors.typographyPalette.primary};
`;
const MonthSelectorWrapper = styled.div`
  position: relative;
`;
const MonthSelectorIcon = styled(ChevronDown)`
  color: ${({theme}) => theme.colors.typographyPalette.secondary};
  width: 20px;
  height: 20px;
`;
const MonthLeft = styled(ChevronLeft)`
  width: 20px;
  height: 20px;
  color: ${({theme}) => theme.colors.typographyPalette.secondary};
  cursor: pointer;
`;
const MonthRight = styled(ChevronRight)`
  width: 20px;
  height: 20px;
  color: ${({theme}) => theme.colors.typographyPalette.secondary};
  cursor: pointer;
`;
const ButtonContainer = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 12px;
`;
const CancelButton = styled(RoundButton)`
  flex: 1;
`;
const ConfirmButton = styled(RoundButton)`
  flex: 1;
`;
const Tip = styled(Label3)`
  color: ${({theme}) => theme.colors.typographyPalette.primary};
  margin-bottom: 12px;
  font-weight: 400;
`;

const Picker = ({
  startDate,
  endDate,
  value,
  onChange,
  multi=false,
  disabled,
  dismiss,
  min,
  tipText=''
}) => {
  const defaultValue = multi ? (value[0] || startDate) : value || startDate;

  const [currentDate, setCurrentDate] = useState(startOfMonth(parse(defaultValue, 'yyyy-MM-dd', new Date())));
  const [localValue, setLocalValue] = useState(value);

  const formattedStartDate = format(parse(startDate, 'yyyy-MM-dd', new Date()), 'yyyy-MM-dd');
  const formattedMinDate = min ? format(parse(min, 'yyyy-MM-dd', new Date()), 'yyyy-MM-dd') : null;
  const weeks = getWeeksToDisplay(currentDate, false);
  const weeksDates = Array.from({length: weeks}, (_, index) => addWeeks(startOfWeek(currentDate), index));

  const handleDateClick = (date) => {
    if (multi) {
      if ([startDate, endDate].includes(date) || isBefore(date, startDate) || isAfter(date, endDate)) return;

      if (localValue.includes(date)) {
        setLocalValue(localValue.filter(selectedDate => selectedDate !== date));
        return;
      }
      setLocalValue([...localValue, date]);
    } else {
      if (min && isBefore(date, addDays(parse(min, 'yyyy-MM-dd', new Date()), -1))) {
        return;
      }
      if (endDate && isAfter(date, endDate)) {
        return;
      }
      setLocalValue(date);

    }
  };

  const handleHeaderClick = (index) => {
    if (multi) {
      const totalWeeksUntilEnd = Math.ceil(differenceInDays(endDate, startDate) / 7);
      const selectedDatesOnDay = Array.from({length: totalWeeksUntilEnd + 1}, (_, index) => addWeeks(startOfWeek(startDate), index)).map(weekStart => format(addDays(weekStart, index), 'yyyy-MM-dd')).filter(date => date > startDate && date < endDate);
      if (selectedDatesOnDay.some(selectedDate => localValue.includes(selectedDate))) {
        setLocalValue(localValue.filter(selectedDate => !selectedDatesOnDay.includes(selectedDate)));
        return;
      }
      setLocalValue([...localValue, ...selectedDatesOnDay.filter(selectedDate => !localValue.includes(selectedDate))]);
    } else {
      // do nothing
    }
  };

  const checkIsSelected = (date) => {
    if (multi) {
      return localValue?.includes(date);
    } else {
      return localValue === date;
    }
  };

  const checkAvailable = (date) => {
    if (multi) {
      return date > formattedStartDate && date < endDate;
    } else {
      if (endDate) {
        return date >= formattedStartDate && date < endDate;
      }
      return date >= formattedMinDate;
    }
  };

  const handleMonthSkip = direction => {
    const newDate = direction === 'left' ? addMonths(currentDate, -1) : addMonths(currentDate, 1);
    if (isBefore(newDate, startOfMonth(new Date()))) return;
    setCurrentDate(newDate);
  };

  const handleConfirm = () => {
    onChange(localValue);
    dismiss();
  };

  return (
    <CalendarContainer>
      <StyledCalendarHeaderContainer>
        <StyledCalendarHeaderRow>
          <StyledCalendarHeaderMonth>
            <MonthLeft onClick={() => handleMonthSkip('left')}/>
            <MonthSelectorWrapper>
              <MonthSelector type="month" disabled value={format(currentDate, 'yyyy-MM')} onChange={e => e.target.value && setCurrentDate(parse(e.target.value, 'yyyy-MM', new Date()))} />
              <FormattedMonthDisplay>
                {format(currentDate, 'MMM yyyy')}
                {/* <MonthSelectorIcon/> */}
              </FormattedMonthDisplay>
            </MonthSelectorWrapper>
            <MonthRight onClick={() => handleMonthSkip('right')}/>
          </StyledCalendarHeaderMonth>
        </StyledCalendarHeaderRow>
          {!!tipText &&
            <Tip>{tipText}</Tip>
          }
        <StyledCalendarHeaderDayRow>
          {Array.from({length: 7}, (_, index) => {
            const allPossibleDaysAreSelected = multi && Array.from({length: 7}, (_, index) => addWeeks(startOfWeek(startDate), index)).map(weekStart => format(addDays(weekStart, index), 'yyyy-MM-dd')).filter(date => date > startDate && date < endDate).every(date => localValue.includes(date));
            const dayFormat = window.innerWidth <= 768 ? 'EEEEE' : 'E';

            return (
              <StyledCalendarHeaderDay
                multi={multi}
                children={format(addDays(startOfWeek(currentDate), index), dayFormat)}
                onClick={() => handleHeaderClick(index)}
                isSelected={allPossibleDaysAreSelected}
              />
            );
          })}

        </StyledCalendarHeaderDayRow>
      </StyledCalendarHeaderContainer>
      <StyledCalendar>
        {weeksDates.map((weekStart, index) => (
          <StyledCalendarWeek>
            {Array.from({length: 7}, (_, dayIndex) => {
              const date = addDays(weekStart, dayIndex);
              const formattedDate = format(date, 'yyyy-MM-dd');
              const isAvailable = checkAvailable(formattedDate);

              return (
                <StyledMobileCalendarDay
                  onClick={() => handleDateClick(formattedDate)}
                  isPresent={isAvailable}
                  isSelected={checkIsSelected(formattedDate)}
                  children={getDate(date)}
                  multi={multi && isAvailable}
                />
              );
            })}
          </StyledCalendarWeek>
        ))}
      </StyledCalendar>
      <ButtonContainer>
        <CancelButton mini white onClick={dismiss}>Cancel</CancelButton>
        <ConfirmButton mini onClick={handleConfirm}>Confirm</ConfirmButton>
      </ButtonContainer>
    </CalendarContainer>
  )
}

// Custom date picker component
const DatePicker = ({
  value,
  startDate,
  min,
  endDate,
  onChange,
  multi = false,
  disabled,
  placeholder="Select a date",
  type='input',
  containerId='modal-container',
  tipText,
}) => {
  const [showCalendar, setShowCalendar] = useState(false);
  const [calendarPosition, setCalendarPosition] = useState({}); // "below" or "above"
  const inputRef = useRef(null);
  const calendarRef = useRef(null);

  // Handle input focus to show calendar
  const handleFocus = () => {
    if (disabled) return;
    setShowCalendar(true);
    adjustCalendarPosition();
    inputRef?.current?.focus();
  };

  const adjustCalendarPosition = () => {
    const inputRect = inputRef?.current?.getBoundingClientRect();
    const modalContainer = document.getElementById(containerId);
    const containerRect = modalContainer?.getBoundingClientRect();
    const calendarRect = {width: 392, height: 307};// calendarRef?.current?.getBoundingClientRect();

    if (inputRect && containerRect && calendarRect) {
      const calendarHeight = calendarRect?.height;
      const spaceBelow = window.innerHeight - (inputRect.bottom + calendarRect.height)
      const spaceAbove = inputRect.top - containerRect.top;

      let position = {};

      if (window.innerWidth < 425) {
        setCalendarPosition({position: 'fixed', bottom: '0',  left: '0'});
        return;
      }

      // top / bottom
      if (spaceBelow < calendarHeight && spaceAbove > calendarHeight) {
        position = {
          top: "auto",
          bottom: "calc(100% + 4px)",
        };
      } else {
        position = {
          top: "calc(100% + 4px)",
          bottom: "auto",
        };
      }

      // left / right
      if ((inputRect.left + calendarRect.width) > containerRect.right) {
        position.left = `calc(100% - ${calendarRect.width}px)`;
      }

      setCalendarPosition(position);
    }
    if (document.documentElement.clientWidth <= 768) {
      setCalendarPosition({top: 'auto', bottom: 'calc(100% + 4px)', left: 0});
    }
  };

  const formatInputValue = (value) => {
    try {
      return multi
        ? value?.length
          ? `${value?.length} Day${value.length === 1 ? '' : 's'} Off`
          : ""
        : value ? format(parse(value, 'yyyy-MM-dd', new Date()), 'MMM do, yyyy') : '';

    } catch (error) {
      return '';
    }
  };

  const handleKeyDown = (e) => {
    if (disabled) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setShowCalendar(true);
      adjustCalendarPosition();
    } else if (e.key === 'Escape') {
      setShowCalendar(false);
    }
  };


  // Close calendar when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        inputRef?.current &&
        !inputRef?.current?.contains(e.target) &&
        !calendarRef?.current?.contains(e.target)
      ) {
        setShowCalendar(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [inputRef.current]);

  return (
    <Container onKeyDown={handleKeyDown}>
      {type === 'input'
        ? <InputWrapper>
            <StyledInput
              type="text"
              ref={inputRef}
              tabIndex={0}
              value={formatInputValue(value)}
              onFocus={handleFocus}
              readOnly
              placeholder={placeholder}
              disabled={disabled}
            />
            <CalendarIcon/>
          </InputWrapper>
        : <InlineWrapper ref={inputRef} onClick={handleFocus} tabIndex={0}>
            <CalendarIconBare/>
            <InlineText>{formatInputValue(value)}</InlineText>
          </InlineWrapper>
      }
      {showCalendar && (
        <PickerWrapper
          ref={calendarRef}
          style={{
            position: "absolute",
            left: 0,
            ...calendarPosition,
          }}
        >
          <Picker
            startDate={startDate}
            endDate={endDate}
            value={value}
            onChange={onChange}
            multi={multi}
            min={min}
            dismiss={() => setShowCalendar(false)}
            tipText={tipText}
          />
        </PickerWrapper>
      )}
    </Container>
  );
};

export default DatePicker;